import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { FaArrowRight, FaCode, FaShoppingCart, FaRocket, FaExternalLinkAlt, FaGithub, FaProjectDiagram, FaUsers, FaStar, FaLaptopCode, FaQuoteLeft, FaCheckCircle } from 'react-icons/fa';
import Button from '../components/Button';
import heroImage from '../assets/hero.jpg';
import BookAppointment from '../components/BookAppointment';
import logo from '../assets/logo.png';

const HeroSection = styled.section`
  min-height: 100vh;
  display: flex;
  align-items: center;
  background: linear-gradient(135deg, #000000 0%, #1a1a1a 100%);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse"><path d="M 10 0 L 0 0 0 10" fill="none" stroke="rgba(255,215,0,0.1)" stroke-width="1"/></pattern></defs><rect width="100" height="100" fill="url(%23grid)"/></svg>');
    opacity: 0.3;
  }
`;

const HeroContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  position: relative;
  z-index: 1;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
    text-align: center;
  }
`;

const HeroContent = styled(motion.div)`
  color: white;
`;

const HeroTitle = styled(motion.h1)`
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: 800;
  line-height: 1.2;
  margin-bottom: 1.5rem;
  
  span {
    color: #4682B4;
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: clamp(1.1rem, 3vw, 1.4rem);
  line-height: 1.6;
  margin-bottom: 2rem;
  opacity: 0.9;
  color: rgba(255, 255, 255, 0.95);
`;

const HeroButtons = styled(motion.div)`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const HeroImage = styled(motion.div)`
  position: relative;
  
  @media (max-width: 768px) {
    order: -1;
  }
`;

const HeroCard = styled(motion.div)`
  background: rgba(255, 215, 0, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 3rem;
  border: 1px solid rgba(70, 130, 180, 0.2);
  text-align: center;
`;

const HeroIcon = styled.div`
  font-size: 4rem;
  color: #4682B4;
  margin-bottom: 1rem;
`;

const FeaturedSection = styled.section`
  padding: 5rem 0;
  background: #0a0a0a;
`;

const FeaturedContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const SectionTitle = styled(motion.h2)`
  text-align: center;
  font-size: clamp(2rem, 4vw, 3rem);
  margin-bottom: 3rem;
  color: #ffffff;
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
`;

const FeatureCard = styled(motion.div)`
  background: #1a1a1a;
  padding: 2rem;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  text-align: center;
  transition: transform 0.3s ease;
  border: 1px solid rgba(255, 215, 0, 0.1);
  
  &:hover {
    transform: translateY(-10px);
    border-color: rgba(70, 130, 180, 0.3);
  }
`;

const FeatureIcon = styled.div`
  font-size: 3rem;
  color: #4682B4;
  margin-bottom: 1rem;
`;

const FeatureTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #ffffff;
`;

const FeatureDescription = styled.p`
  color: #cccccc;
  line-height: 1.6;
`;

const ProjectsSection = styled.section`
  padding: 5rem 0;
  background: #0a0a0a;
`;

const ProjectsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ProjectCard = styled(motion.div)`
  background: #1a1a1a;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
  cursor: pointer;
  border: 1px solid rgba(255, 215, 0, 0.1);
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
    border-color: rgba(70, 130, 180, 0.3);
  }
`;

const ProjectImage = styled.div`
  height: 200px;
  background: linear-gradient(135deg, #4682B4 0%, #87CEEB 100%);
  position: relative;
  overflow: hidden;
  
  .image-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #000000;
    font-size: 1rem;
    text-align: center;
    padding: 1.5rem;
  }
  
  .project-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover .project-overlay {
    opacity: 1;
  }
`;

const ProjectContent = styled.div`
  padding: 1.5rem;
`;

const ProjectTitle = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 0.5rem;
  color: #ffffff;
`;

const ProjectDescription = styled.p`
  color: #cccccc;
  line-height: 1.6;
  margin-bottom: 1rem;
  font-size: 0.9rem;
`;

const ProjectTech = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const TechTag = styled.span`
  background: #2a2a2a;
  color: #cccccc;
  padding: 0.2rem 0.6rem;
  border-radius: 12px;
  font-size: 0.8rem;
  border: 1px solid rgba(70, 130, 180, 0.2);
`;

const ProjectLinks = styled.div`
  display: flex;
  gap: 1rem;
`;

const ProjectLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 45px;
  height: 45px;
  background: #4682B4;
  border-radius: 50%;
  color: #000000;
  text-decoration: none;
  transition: all 0.3s ease;
  
  &:hover {
    background: #87CEEB;
    transform: scale(1.1);
  }
`;

const ViewAllButton = styled.div`
  text-align: center;
  margin-top: 3rem;
`;

const StatsSection = styled.section`
  padding: 5rem 0;
  background: linear-gradient(135deg, #1a1a1a 0%, #0a0a0a 100%);
`;

const StatsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;
`;

const StatCard = styled(motion.div)`
  text-align: center;
  padding: 2rem;
  background: rgba(255, 215, 0, 0.05);
  border-radius: 15px;
  border: 1px solid rgba(70, 130, 180, 0.2);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    background: rgba(70, 130, 180, 0.1);
    border-color: rgba(255, 215, 0, 0.4);
  }
`;

const StatIcon = styled.div`
  font-size: 2.5rem;
  color: #4682B4;
  margin-bottom: 1rem;
`;

const StatNumber = styled.div`
  font-size: 3rem;
  font-weight: 800;
  color: #ffffff;
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  font-size: 1.1rem;
  color: #cccccc;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const TestimonialsSection = styled.section`
  padding: 5rem 0;
  background: #0a0a0a;
`;

const TestimonialsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const TestimonialsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const TestimonialCard = styled(motion.div)`
  background: #1a1a1a;
  padding: 2rem;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 215, 0, 0.1);
  position: relative;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    border-color: rgba(70, 130, 180, 0.3);
  }
`;

const QuoteIcon = styled.div`
  font-size: 2rem;
  color: rgba(255, 215, 0, 0.3);
  margin-bottom: 1rem;
`;

const TestimonialText = styled.p`
  color: #cccccc;
  line-height: 1.6;
  margin-bottom: 1.5rem;
  font-style: italic;
`;

const TestimonialAuthor = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const AuthorAvatar = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: linear-gradient(135deg, #4682B4 0%, #87CEEB 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #000000;
  font-weight: bold;
  font-size: 1.2rem;
`;

const AuthorInfo = styled.div`
  flex: 1;
`;

const AuthorName = styled.h4`
  color: #ffffff;
  margin-bottom: 0.25rem;
`;

const AuthorRole = styled.p`
  color: #cccccc;
  font-size: 0.9rem;
`;

const Rating = styled.div`
  display: flex;
  gap: 0.25rem;
  margin-bottom: 1rem;
`;

const Star = styled(FaStar)`
  color: #ffd700;
  font-size: 1rem;
`;

const ClientsSection = styled.div`
  margin-top: 3rem;
  text-align: center;
`;

const ClientsTitle = styled.h3`
  color: #ffffff;
  margin-bottom: 2rem;
  font-size: 1.5rem;
`;

const ClientsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 2rem;
  align-items: center;
`;

const ClientLogo = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 80px;
  color: #cccccc;
  font-weight: 500;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(70, 130, 180, 0.1);
    border-color: rgba(70, 130, 180, 0.3);
    color: #4682B4;
  }
`;

const Home = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  const features = [
    {
      icon: <FaCode />,
      title: "Custom Development",
      description: "Tailored web solutions built with modern technologies to meet your specific business needs."
    },
    {
      icon: <FaShoppingCart style={{ color: '#25D366' }} />,
      title: "E-commerce Solutions",
      description: "Powerful online stores with Shopify, WooCommerce, and custom e-commerce platforms."
    },
    {
      icon: <FaRocket style={{ color: '#DC143C' }} />,
      title: "Performance Optimized",
      description: "Lightning-fast websites optimized for speed, SEO, and exceptional user experience."
    }
  ];

  const featuredProjects = [
    {
      id: 1,
      title: "Kaj Outfits",
      description: "A trendy fashion e-commerce platform built on Shopify, specializing in modern outfits and accessories.",
      image: "kaj-outfits",
      technologies: ["Shopify", "Liquid", "JavaScript", "CSS"],
      liveLink: "https://kajoutfits.com",
      githubLink: "https://github.com"
    },
    {
      id: 2,
      title: "Loom & Local",
      description: "A comprehensive multi-vendor e-commerce platform built with React and Laravel.",
      image: "loomandlocal",
      technologies: ["React", "Laravel", "MySQL", "PHP"],
      liveLink: "https://www.loomandlocal.com",
      githubLink: "https://github.com"
    },
    {
      id: 3,
      title: "ShoppetX",
      description: "A modern Shopify e-commerce store featuring trending products and multi-language support.",
      image: "shoppetx",
      technologies: ["Shopify", "Liquid", "JavaScript", "CSS"],
      liveLink: "https://shoppetx.com",
      githubLink: "https://github.com"
    }
  ];

  const stats = [
    {
      icon: <FaProjectDiagram style={{ color: '#25D366' }} />,
      number: "50+",
      label: "Projects Completed"
    },
    {
      icon: <FaLaptopCode />,
      number: "5+",
      label: "Years Experience"
    },
    {
      icon: <FaUsers style={{ color: '#8B4789' }} />,
      number: "30+",
      label: "Happy Clients"
    },
    {
      icon: <FaStar style={{ color: '#ffd700' }} />,
      number: "15+",
      label: "Technologies Mastered"
    }
  ];

  const testimonials = [
    {
      id: 1,
      text: "Softvance Solution delivered an exceptional e-commerce platform that exceeded our expectations. His attention to detail and technical expertise made our vision a reality.",
      author: "Ayesha Khan",
      role: "CEO, Kaj Outfits",
      avatar: "AK",
      rating: 5
    },
    {
      id: 2,
      text: "Working with Softvance Solution was a game-changer for our business. He transformed our online presence and increased our sales by 200%.",
      author: "Ahmed Hassan",
      role: "Founder, Loom & Local",
      avatar: "AH",
      rating: 5
    },
    {
      id: 3,
      text: "Professional, reliable, and incredibly talented. Softvance Solution built our Shopify store with features we didn't even know were possible.",
      author: "Fatima Shah",
      role: "Marketing Director, ShoppetX",
      avatar: "FS",
      rating: 5
    }
  ];

  const clients = [
    "Kaj Outfits",
    "Loom & Local",
    "ShoppetX",
    "Icon Creativity",
    "Tech Startups",
    "Local Businesses"
  ];

  return (
    <>
      <HeroSection>
        <HeroContainer>
          <HeroContent
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <HeroTitle
              variants={itemVariants}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '20px', paddingLeft: '20px' }}>
                <img src={logo} alt="Softvance Solution" style={{ height: '80px', width: 'auto' }} />
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ color: '#ffffff', fontWeight: '800', fontSize: '3.5rem', lineHeight: '1' }}>SOFTVANCE</span>
                  <span style={{ color: '#4682B4', fontWeight: '800', fontSize: '2rem', lineHeight: '1' }}>SOLUTION</span>
                </div>
              </div>
            </HeroTitle>
            <HeroSubtitle
              variants={itemVariants}
            >
              I'm a passionate Web Developer specializing in creating responsive, 
              high-performance websites that help businesses thrive in the digital world.
            </HeroSubtitle>
            <HeroButtons
              variants={itemVariants}
            >
              <Button as={Link} to="/portfolio">
                View My Work <FaArrowRight style={{ marginLeft: '0.5rem' }} />
              </Button>
              <Button variant="outline" as={Link} to="/contact">
                Get In Touch
              </Button>
            </HeroButtons>
          </HeroContent>

          <HeroImage
            variants={itemVariants}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <img 
              src={heroImage} 
              alt="Softvance Solution Hero"
              style={{ 
                width: '100%', 
                height: 'auto', 
                borderRadius: '15px',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)'
              }} 
            />
          </HeroImage>
        </HeroContainer>
      </HeroSection>

      <FeaturedSection>
        <FeaturedContainer>
          <SectionTitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            What I Do Best
          </SectionTitle>
          <FeatureGrid>
            {features.map((feature, index) => (
              <FeatureCard
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
              >
                <FeatureIcon>{feature.icon}</FeatureIcon>
                <FeatureTitle>{feature.title}</FeatureTitle>
                <FeatureDescription>{feature.description}</FeatureDescription>
              </FeatureCard>
            ))}
          </FeatureGrid>
        </FeaturedContainer>
      </FeaturedSection>

      <ProjectsSection>
        <ProjectsContainer>
          <SectionTitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Featured Projects
          </SectionTitle>
          <ProjectsGrid>
            {featuredProjects.map((project, index) => (
              <ProjectCard
                key={project.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ scale: 1.02 }}
              >
                <ProjectImage>
                  <div className="image-placeholder">
                    <div>
                      <h4>{project.title}</h4>
                      <p>Project Preview</p>
                    </div>
                  </div>
                  <div className="project-overlay">
                    <ProjectLinks>
                      <ProjectLink 
                        href={project.liveLink} 
                        target="_blank" 
                        rel="noopener noreferrer"
                      >
                        <FaExternalLinkAlt />
                      </ProjectLink>
                      <ProjectLink 
                        href={project.githubLink} 
                        target="_blank" 
                        rel="noopener noreferrer"
                      >
                        <FaGithub />
                      </ProjectLink>
                    </ProjectLinks>
                  </div>
                </ProjectImage>
                <ProjectContent>
                  <ProjectTitle>{project.title}</ProjectTitle>
                  <ProjectDescription>{project.description}</ProjectDescription>
                  <ProjectTech>
                    {project.technologies.map((tech, techIndex) => (
                      <TechTag key={techIndex}>{tech}</TechTag>
                    ))}
                  </ProjectTech>
                </ProjectContent>
              </ProjectCard>
            ))}
          </ProjectsGrid>
          <ViewAllButton>
            <Button as={Link} to="/portfolio">
              View All Projects <FaArrowRight style={{ marginLeft: '0.5rem' }} />
            </Button>
          </ViewAllButton>
        </ProjectsContainer>
      </ProjectsSection>

      <StatsSection>
        <StatsContainer>
          <SectionTitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Numbers That Matter
          </SectionTitle>
          <StatsGrid>
            {stats.map((stat, index) => (
              <StatCard
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
              >
                <StatIcon>{stat.icon}</StatIcon>
                <StatNumber>{stat.number}</StatNumber>
                <StatLabel>{stat.label}</StatLabel>
              </StatCard>
            ))}
          </StatsGrid>
        </StatsContainer>
      </StatsSection>

      <TestimonialsSection>
        <TestimonialsContainer>
          <SectionTitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            What Clients Say
          </SectionTitle>
          <TestimonialsGrid>
            {testimonials.map((testimonial, index) => (
              <TestimonialCard
                key={testimonial.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ scale: 1.02 }}
              >
                <QuoteIcon>
                  <FaQuoteLeft />
                </QuoteIcon>
                <Rating>
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} />
                  ))}
                </Rating>
                <TestimonialText>{testimonial.text}</TestimonialText>
                <TestimonialAuthor>
                  <AuthorAvatar>{testimonial.avatar}</AuthorAvatar>
                  <AuthorInfo>
                    <AuthorName>{testimonial.author}</AuthorName>
                    <AuthorRole>{testimonial.role}</AuthorRole>
                  </AuthorInfo>
                </TestimonialAuthor>
              </TestimonialCard>
            ))}
          </TestimonialsGrid>
          
          <ClientsSection>
            <ClientsTitle>Trusted by Leading Brands</ClientsTitle>
            <ClientsGrid>
              {clients.map((client, index) => (
                <ClientLogo
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.05 }}
                  whileHover={{ scale: 1.1 }}
                >
                  {client}
                </ClientLogo>
              ))}
            </ClientsGrid>
          </ClientsSection>
        </TestimonialsContainer>
      </TestimonialsSection>

      <BookAppointment />
    </>
  );
};

export default Home;
